/**
 * Hazard pins on a Teslyn Cloud map (Diurnal parity 05-022; docs/specs/map.md §16.12; docs/specs/cloud/
 * live-tracking.md §2): drawn as Teslyn Field draws them (`apps/field/src/map/hazardPinLayer.ts`), a circle in the
 * pin's colour with its icon in white, from the same domain icons and colours (`pinIcon`, `pinColour`). Read only on
 * the Cloud (pins are placed and moved in Teslyn Field). Names reach the map only as text nodes; the pin's colour is
 * data the crew chose, like a KML file's own colours.
 */
import L from 'leaflet';
import {
  PIN_ICON_INK,
  pinColour,
  pinIcon,
  pinIdentifyRows,
  pinTooltip,
  type HazardPinRecord,
} from '@teslyn/domain';
import { textNode } from './leaflet.ts';

/** The pin's side on the map, as Teslyn Field's (`--map-control-point-size`). */
const PIN_PX = 22;

const SVG = 'http://www.w3.org/2000/svg';

/** The pin's icon: a circle of its colour with the icon in white. */
export function pinIconElement(icon: string, colour: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = 'map-hazard-pin';
  el.style.background = pinColour(icon, colour);
  el.style.borderColor = PIN_ICON_INK;
  el.setAttribute('aria-hidden', 'true');
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'map-hazard-pin__icon');
  const path = document.createElementNS(SVG, 'path');
  path.setAttribute('d', pinIcon(icon).path);
  path.setAttribute('fill', PIN_ICON_INK);
  svg.appendChild(path);
  el.appendChild(svg);
  return el;
}

/** A popup of label and value rows (text nodes only). */
export function identifyPopup(
  rows: ReadonlyArray<{ readonly label: string; readonly value: string }>,
): HTMLElement {
  const el = document.createElement('dl');
  el.className = 'map-identify';
  for (const r of rows) {
    el.append(textNode(r.label, 'caption muted map-identify__label'));
    el.append(textNode(r.value, 'caption map-identify__value'));
  }
  return el;
}

/** Draw the pins in `g` (cleared first), in `pane` when given. */
export function drawHazardPins(g: L.LayerGroup, pins: readonly HazardPinRecord[], pane?: string): void {
  g.clearLayers();
  for (const p of pins) {
    const marker = L.marker([p.lat, p.lng], {
      icon: L.divIcon({
        html: pinIconElement(p.icon, p.colour),
        className: 'map-hazard-pin-icon',
        iconSize: [PIN_PX, PIN_PX],
        iconAnchor: [PIN_PX / 2, PIN_PX / 2],
      }),
      keyboard: true,
      title: p.name,
      alt: p.name,
      ...(pane ? { pane } : {}),
    })
      .bindTooltip(textNode(pinTooltip(p)), { direction: 'top', offset: [0, -PIN_PX / 2] })
      .bindPopup(() => identifyPopup(pinIdentifyRows(p)), { maxWidth: 320 });
    g.addLayer(marker);
  }
}
